import { observer } from 'mobx-react-lite';

import { Button, XStack, YStack } from '@/components/ui';
import { useTabs } from '@/features/tabs';
import { logError } from '@/lib/errorLogging';
import { cn } from '@/lib/utils';

import { startChatPromptCard } from '../start-chat-prompt-card';

import type { WaitingForYouState } from '../use-waiting-for-you';
import type { ChatPromptCardEntry } from '@littlebird/action-cards';

type HomeChatPromptCardPaneProps = {
  readonly entry: ChatPromptCardEntry;
  readonly home: WaitingForYouState;
  readonly fadeActions?: boolean;
};

export const HomeChatPromptCardPane = observer(
  ({ entry, home, fadeActions = false }: HomeChatPromptCardPaneProps) => {
    const { openTab } = useTabs();
    const cardId = entry.card.id;
    if (home.deferredDismiss.isDeferred(cardId)) return null;

    const startChat = () =>
      void startChatPromptCard(entry, openTab).catch((error: unknown) =>
        logError(error, 'Failed to start chat from action card', {
          context: 'ChatPromptCard:startChat',
          metadata: { cardId },
        })
      );

    return (
      <YStack className="overflow-hidden rounded-xl border border-border-strong bg-surface shadow-sm">
        <XStack className="items-baseline gap-2 border-b border-border-subtle px-4 py-2.5">
          <span className="text-xs text-foreground-subtle">
            Created from the Littlebird admin panel because you're part of a
            special testing cohort.
          </span>
        </XStack>
        {entry.content.body ? (
          <YStack className="px-4 py-3">
            <span
              data-find-field="body"
              className="text-sm text-foreground-muted"
            >
              {entry.content.body}
            </span>
          </YStack>
        ) : null}
        <XStack
          className={cn(
            'justify-end gap-2 border-t border-border-subtle px-4 py-3',
            fadeActions &&
              'transition-opacity duration-150 motion-reduce:transition-none [@media(hover:hover)]:opacity-0 group-hover/feed-card:opacity-100 group-focus-within/feed-card:opacity-100'
          )}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => home.deferDismiss([entry])}
          >
            Dismiss
          </Button>
          <Button size="sm" onClick={startChat}>
            Start chat
          </Button>
        </XStack>
      </YStack>
    );
  }
);
